/**
 * Utility functions for handling notifications in the application
 */

// Store for active notifications
let notificationStore = [];
let notificationId = 0;
let notificationListeners = [];

/**
 * Notification types
 */
export const NotificationType = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

/**
 * Add a notification to the store
 * @param {Object} notification - Notification object
 * @param {string} notification.type - Type of notification (success, error, warning, info)
 * @param {string} notification.message - Message to display
 * @param {string} notification.title - Optional title for the notification
 * @param {number} notification.duration - Duration in milliseconds to show the notification (default: 5000)
 * @returns {number} Notification ID
 */
export const addNotification = ({ type, message, title, duration = 5000 }) => {
  const id = notificationId++;
  const notification = {
    id,
    type,
    message,
    title,
    duration,
    timestamp: Date.now()
  };
  
  notificationStore = [...notificationStore, notification];
  notifyListeners();
  
  // Auto-remove after duration
  if (duration > 0) {
    setTimeout(() => {
      removeNotification(id);
    }, duration);
  }
  
  return id;
};

/**
 * Remove a notification from the store
 * @param {number} id - Notification ID to remove
 */
export const removeNotification = (id) => {
  notificationStore = notificationStore.filter(notification => notification.id !== id);
  notifyListeners();
};

/**
 * Clear all notifications
 */
export const clearAllNotifications = () => {
  notificationStore = [];
  notifyListeners();
};

/**
 * Subscribe to notification changes
 * @param {Function} listener - Callback function to call when notifications change
 * @returns {Function} Unsubscribe function
 */
export const subscribeToNotifications = (listener) => {
  notificationListeners.push(listener);
  
  // Call listener immediately with current state
  listener(notificationStore);
  
  // Return unsubscribe function
  return () => {
    notificationListeners = notificationListeners.filter(l => l !== listener);
  };
};

/**
 * Notify all listeners of changes
 */
const notifyListeners = () => {
  notificationListeners.forEach(listener => {
    listener(notificationStore);
  });
};

/**
 * Helper functions for common notification types
 */

/**
 * Show a success notification
 * @param {string} message - Message to display
 * @param {string} title - Optional title
 * @param {number} duration - Duration in milliseconds
 * @returns {number} Notification ID
 */
export const showSuccess = (message, title = 'Success', duration = 5000) => {
  return addNotification({
    type: NotificationType.SUCCESS,
    message,
    title,
    duration
  });
};

/**
 * Show an error notification
 * @param {string} message - Message to display
 * @param {string} title - Optional title
 * @param {number} duration - Duration in milliseconds
 * @returns {number} Notification ID
 */
export const showError = (message, title = 'Error', duration = 8000) => {
  return addNotification({
    type: NotificationType.ERROR,
    message,
    title,
    duration
  });
};

/**
 * Show a warning notification
 * @param {string} message - Message to display
 * @param {string} title - Optional title
 * @param {number} duration - Duration in milliseconds
 * @returns {number} Notification ID
 */
export const showWarning = (message, title = 'Warning', duration = 7000) => {
  return addNotification({
    type: NotificationType.WARNING,
    message,
    title,
    duration
  });
};

/**
 * Show an info notification
 * @param {string} message - Message to display
 * @param {string} title - Optional title
 * @param {number} duration - Duration in milliseconds
 * @returns {number} Notification ID
 */
export const showInfo = (message, title = 'Information', duration = 5000) => {
  return addNotification({
    type: NotificationType.INFO,
    message,
    title,
    duration
  });
};

/**
 * Handle API error and show appropriate notification
 * @param {Error} error - Error object
 * @param {string} fallbackMessage - Fallback message if error doesn't have a message
 */
export const handleApiError = (error, fallbackMessage = 'An error occurred while processing your request') => {
  const errorMessage = error.response?.data?.message || error.message || fallbackMessage;
  showError(errorMessage);
  console.error('API Error:', error);
};
